import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Alert,
  Avatar,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NoPhotographyOutlinedIcon from "@mui/icons-material/NoPhotographyOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print"; // UI only, function might not exist yet
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NoteIcon from "@mui/icons-material/Note";

import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { useCustomers } from "../../hooks/useCustomers";
import { useProductsBranch } from "../../hooks/useProductsBranch";
import * as services from "../../services/saleng";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { formatNumber } from "../../utils/formatNumber";
import ProductSelectDialog from "../../components/ProductSelectDialog";
import { jwtDecode } from "jwt-decode";
import { hasPermission } from "../../auth/RequirePermission3";

const getAccessToken = () => localStorage.getItem("accessToken") || null;

const decodeToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const getTokenData = () => {
  const decoded = decodeToken();
  return {
    role: decoded?.role ?? null,
  };
};

const SoManagementEdit = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // --- Data Hooks ---
  const { customers, loading: loadingCust } = useCustomers();
  const { products, loading: loadingProd } = useProductsBranch();

  // --- States ---
  const [so, setSo] = useState(null);
  const [rowsData, setRowsData] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState("");
  const [soDetail, setSoDetail] = useState(null);

  const [openCustDialog, setOpenCustDialog] = useState(false);
  const [modalStep, setModalStep] = useState("customer");
  const [selCustomer, setSelCustomer] = useState(null);
  const [selCar, setSelCar] = useState(null);
  const [snackNoCar, setSnackNoCar] = useState(false);
  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState("");
  const [detail2, setDetail2] = useState("");
  const [searchParams] = useSearchParams();
  const [searchCustomer, setSearchCustomer] = useState("");

  const soId = searchParams.get("id");
  const { role } = getTokenData();

  const statusMap = {
    open: "เปิด",
    close: "สิ้นสุด",
    cancel: "ยกเลิก",
    draft: "ร่าง",
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-green-100 text-green-700 border-green-200";
      case "close":
        return "bg-red-100 text-red-700 border-red-200";
      case "cancel":
        return "bg-gray-100 text-gray-500 border-gray-200";
      case "draft":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const isClosedOrCanceled = ["close", "cancel"].includes(statusFilter);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      while (isMounted && (loadingProd || products.length === 0)) {
        await new Promise((r) => setTimeout(r, 100));
      }
      if (!isMounted) return;

      try {
        const res = await services.getSaleOrders({ id: soId });
        const data = res.data?.data || res.data || res;
        const soData = Array.isArray(data) ? data[0] : data;
        
        if (!soData) return;

        setSoDetail(soData);
        setSo(soData);

        const updatedRows = (soData.items || []).map((item) => {
          const p = products.find((x) => x.id === item.productId);
          return {
            itemId: item.id, // Keep existing item ID for updates
            itemCode: p?.history?.itemCode,
            productId: item.productId,
            detail: p?.subCategory?.name || "",
            product: p?.title || "-",
            unit: "กก.",
            unlimitedWeight: !!item.unlimitedWeight,
            actualWeight: Number(item.actualWeight) || 0,
            weight: Number(item.weight) || 0,
            shortWeight: Number(item.shortWeight) || 0,
            pricePerUnit: Number(item.pricePerUnit) || 0,
            predictPrice: Number(item.predictPrice) || 0,
          };
        });

        const toDateOnly = (date) =>
          date ? new Date(date).toISOString().split("T")[0] : "";

        setSelCustomer(soData.customer || null);
        setSelCar(soData.car || null);
        setStartDate(toDateOnly(soData.startDate));
        setEndDate(toDateOnly(soData.endDate));
        setDetail(soData.detail ?? "");
        setDetail2(soData.detail2 ?? "");
        setRowsData(updatedRows);
        setStatusFilter(soData.status || "draft");

      } catch (err) {
        console.error("Error loading SO:", err);
        toast.error("โหลดข้อมูลใบขายไม่สำเร็จ");
      }
    };

    init();
    return () => { isMounted = false; };
  }, [loadingProd, soId, products]);

  const getMinEndDate = (start) => {
    if (!start) return "";
    const d = new Date(start);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => cat?.name && !seen.has(cat.name) && seen.add(cat.name));
  }, [products]);

  const filteredRows = useMemo(() => {
    const normSearch = search.trim().toLowerCase();
    const filtered = rowsData.filter((r) =>
      r.product?.toLowerCase().includes(normSearch)
    );
    return filtered.sort((a, b) => {
      const A = a?.itemCode ?? "";
      const B = b?.itemCode ?? "";
      if (!A && B) return 1;
      if (A && !B) return -1;
      return String(A).localeCompare(String(B), "th", {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [rowsData, search]);

  const summaryData = useMemo(() => {
    const sumActual = filteredRows.reduce((s, r) => s + (r.actualWeight ?? 0), 0);
    const sumWeight = filteredRows.reduce((s, r) => s + (r.weight ?? 0), 0);
    const sumShort = filteredRows.reduce((s, r) => s + (r.shortWeight ?? 0), 0);
    const sumPredict = filteredRows.reduce((s, r) => s + (r.predictPrice ?? 0), 0);
    return {
        sumActual,
        sumWeight,
        sumShort,
        sumPredict
    };
  }, [filteredRows]);

  const handleConfirmProducts = (selectedProducts) => {
    if (isClosedOrCanceled) return;

    const arr = Array.isArray(selectedProducts) ? selectedProducts : [selectedProducts];
    const pickedIds = arr.map(x => (typeof x === 'object' ? x.id : x));
    
    // Logic: Keep existing if in pickedIds, add new if in pickedIds, remove if NOT in pickedIds
    // Similar to Create logic but preserving itemId
    
    // BUT, ProductSelectDialog with `multi` and `initialSelectedIds` usually handles "selection".
    // If we rely on `selectedProducts` being the FULL LIST of desired products:
    
    const newRows = [];
    const existingMap = new Map(rowsData.map(r => [r.productId, r]));

    for (const pid of pickedIds) {
        const existing = existingMap.get(pid);
        const p = products.find(prod => prod.id === pid);
        if (!p) continue;

        if (existing) {
            newRows.push(existing);
        } else {
             // New Row
             newRows.push({
                itemId: null, // New item
                itemCode: p?.history?.itemCode,
                productId: p.id,
                detail: p.subCategory?.name || "",
                product: p.title,
                unit: "กก.",
                unlimitedWeight: false,
                actualWeight: 0,
                weight: p.weight ?? 0,
                shortWeight: p.weight ?? 0,
                pricePerUnit: p.pricePerUnit ?? 0,
                predictPrice: (p.weight ?? 0) * (p.pricePerUnit ?? 0),
             });
        }
    }
    
    setRowsData(newRows);
    setOpenProdDialog(false);
    setSearch("");
  };

  const updateRow = (id, updates) => {
    if (isClosedOrCanceled) return;

    setRowsData((prev) =>
      prev.map((r) => {
        // id here could be itemId (if exists) or productId if we used productId as key.
        // But in rendered list we use `row.itemId ?? new-{productId}` as key?
        // Let's ensure we identify correctly.
        // In the table mapping below: `key={imgKey}` where `imgKey = row.itemId || row.productId`?
        // Ideally we should use a consistent ID.
        // Let's use `row.productId` as unique identifier for UI manipulation since product can only appear once in SO usually.
        // OR better: match by equality.
        if (r !== id) return r; // Wait, I pass the entire object? No, I pass ID.
        // Let's change the pattern to pass row object or index, or reliable ID.
        // `productId` is reliable enough for this context as duplicates aren't allowed.
        return r; 
      })
    ); 
    // Wait, let's fix the updateRow signature usage.
  };
  
  // Revised update logic
  const handleUpdateRow = (rowIndex, updates) => {
      if (isClosedOrCanceled) return;
      setRowsData(prev => {
          const newData = [...prev];
          const r = { ...newData[rowIndex], ...updates };
          
          const N = (v) => (Number.isFinite(+v) ? +v : 0);
          
          if (r.unlimitedWeight) {
             r.weight = 0;
             r.shortWeight = 0;
             r.predictPrice = 0;
          } else {
             r.weight = N(r.weight);
             r.pricePerUnit = N(r.pricePerUnit);
             r.predictPrice = r.weight * r.pricePerUnit;
             r.shortWeight = r.weight - r.actualWeight;
          }
          
          newData[rowIndex] = r;
          return newData;
      });
  };

  const handleDelete = (index) => {
      if (isClosedOrCanceled) return;
      setRowsData(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (newStatus = null) => {
    const errors = [];
    if (!endDate) errors.push("กรุณากรอกวันที่สิ้นสุด");
    if (!selCustomer) errors.push("กรุณาเลือกลูกค้า");
    if (!rowsData.length) errors.push("กรุณาเลือกสินค้าอย่างน้อย 1 รายการ");
    if (errors.length) {
      errors.forEach((err) => toast.error(err));
      return;
    }

    try {
      const payload = {
        customerId: selCustomer?.id,
        carId: selCar?.id ?? null,
        startDate,
        endDate,
        detail,
        detail2,
        status: newStatus || statusFilter,
        items: rowsData.map((r) => ({
          id: r.itemId || undefined, // Send ID if exists (update), undefined if new (create)
          productId: r.productId,
          weight: r.weight,
          actualWeight: r.actualWeight,
          shortWeight: r.shortWeight,
          pricePerUnit: r.pricePerUnit,
          predictPrice: r.predictPrice,
          unlimitedWeight: r.unlimitedWeight,
        })),
      };

      await services.updateSaleOrders(soId, payload);
      toast.success("บันทึกสำเร็จ");
      if (newStatus === "close" || newStatus === "cancel") {
          setStatusFilter(newStatus);
      }
      // Reload logic? or navigate? PO edits usually reload or stay.
      // Re-fetch to be safe
      const res = await services.getSaleOrders({ id: soId });
      const data = res.data?.data || res.data || res;
      if (Array.isArray(data) && data[0]) {
           // Update local state if needed
           setStatusFilter(data[0].status);
      }
    } catch (err) {
      console.error(err);
      toast.error("เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  if (loadingCust || loadingProd || !so) return <LinearProgress />;

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                <AppBreadcrumbs poNumber={so.poNumber ? so.poNumber : null} />
        
        <Toaster position="bottom-left" reverseOrder={false} />

        {/* 1. Header Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                 <EditIcon />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                แก้ไขใบขาย #{String(soId).padStart(4, '0')}
              </h1>
            </div>
            <p className="text-sm text-gray-500 ml-11">
              แก้ไขข้อมูลและจัดการรายการสินค้าในใบขาย
            </p>
          </div>
          
          <div className={`px-4 py-1.5 rounded-full text-sm font-bold border ${getStatusColor(statusFilter)} flex items-center gap-2`}>
             <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
             {statusMap[statusFilter] || statusFilter}
          </div>
        </div>

        {/* 2. Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col h-full relative group">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">ข้อมูลลูกค้า</h3>
              {selCustomer ? (
                  <div className="flex-1 flex flex-col gap-4">
                      <div className="flex items-center gap-4">
                          <Avatar
                              src={selCustomer.img}
                              sx={{ width: 64, height: 64 }}
                              className="shadow-sm border-4 border-slate-50"
                          >
                              <NoPhotographyOutlinedIcon />
                          </Avatar>
                          <div>
                              <p className="font-bold text-lg text-gray-900 line-clamp-1">{selCustomer.name}</p>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <DirectionsCarIcon fontSize="inherit" />
                                  {selCar ? `ทะเบียน ${selCar.license}` : "ไม่ระบุรถ"}
                              </p>
                          </div>
                      </div>
                      
                      {!isClosedOrCanceled && (
                          <div className="mt-auto flex gap-2">
                              <button
                                  onClick={() => {
                                      setModalStep("customer");
                                      setOpenCustDialog(true);
                                  }}
                                  className="flex-1 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                              >
                                  เปลี่ยนลูกค้า
                              </button>
                          </div>
                      )}
                  </div>
              ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl bg-slate-50 hover:bg-blue-50/50 hover:border-blue-300 transition-all cursor-pointer group/add w-full h-[180px]"
                       onClick={() => !isClosedOrCanceled && setOpenCustDialog(true)}>
                      <div className="p-3 bg-white rounded-full shadow-sm group-hover/add:scale-110 transition-transform text-gray-400 group-hover/add:text-blue-500">
                          <PersonIcon fontSize="large" />
                      </div>
                      <span className="mt-3 text-sm font-medium text-gray-500 group-hover/add:text-blue-600">กดเพื่อเลือกลูกค้า</span>
                  </div>
              )}
          </div>

          {/* Details & Dates */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col h-full">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">เงื่อนไขและวันที่</h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                   สร้างเมื่อ: {new Date(so.createdAt || new Date()).toLocaleDateString('th-TH')}
                </span>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-1">
                  <div className="space-y-4">
                      <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">รายละเอียดเอกสาร</label>
                          <textarea
                             rows={3}
                             disabled={isClosedOrCanceled}
                             className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm resize-none
                                 ${isClosedOrCanceled ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-slate-50 text-gray-700 border-slate-200'}
                             `}
                             placeholder="ระบุรายละเอียดเพิ่มเติม..."
                             value={detail}
                             onChange={(e) => setDetail(e.target.value)}
                          />
                      </div>
                  </div>
                  
                  <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">วันเริ่มต้น</label>
                            <div className="relative">
                                <input
                                  type="date"
                                  value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                  disabled
                                  className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border border-slate-200 rounded-xl text-sm font-medium text-gray-500"
                                />
                                <CalendarTodayIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 opacity-70" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">วันสิ้นสุด</label>
                            <div className="relative">
                                <input
                                  type="date"
                                  value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                  min={getMinEndDate(startDate)}
                                  disabled={isClosedOrCanceled}
                                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm font-medium outline-none
                                     ${isClosedOrCanceled 
                                        ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-200" 
                                        : "bg-white border-slate-200 text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"}
                                  `}
                                />
                                <CalendarTodayIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
        </div>

        {/* 3. Product Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[400px]">
           {/* Toolbar */}
           <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white sticky top-0 z-10">
               <div className="flex items-center gap-3 w-full sm:w-auto">
                    <h3 className="text-lg font-bold text-gray-800">รายการสินค้า</h3>
                    <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-bold">
                        {rowsData.length} รายการ
                    </span>
               </div>
               
               <div className="flex items-center gap-3 w-full sm:w-auto">
                   <div className="relative group w-full sm:w-64">
                       <SearchIcon className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                       <input 
                           type="text" 
                           placeholder="ค้นหาสินค้า..." 
                           className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-sm outline-none transition-all"
                           value={search}
                           onChange={(e) => setSearch(e.target.value)}
                       />
                   </div>
                   
                   {!isClosedOrCanceled && (
                       <button
                            onClick={() => setOpenProdDialog(true)}
                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all active:scale-95 flex items-center gap-2 whitespace-nowrap"
                       >
                            <AddIcon className="w-5 h-5" />
                            <span className="hidden sm:inline">เพิ่มสินค้า</span>
                       </button>
                   )}
               </div>
           </div>

           {/* Table */}
           <div className="overflow-x-auto custom-scrollbar flex-1 bg-white">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100 text-left">
                      <th className="px-4 py-4 w-24">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">รหัส</span>
                      </th>
                      <th className="px-4 py-4 w-32">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">หมวดหมู่</span>
                      </th>
                      <th className="px-6 py-4">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">สินค้า</span>
                      </th>
                      <th className="px-4 py-4 text-center w-36">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">ไม่จำกัด</span>
                      </th>
                      <th className="px-4 py-4 text-right w-40">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">นน.จริง / SO</span>
                      </th>
                      <th className="px-4 py-4 text-right w-32">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">ส่วนต่าง</span>
                      </th>
                      <th className="px-4 py-4 text-right w-40">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">ราคาซื้อ</span>
                      </th>
                      <th className="px-6 py-4 text-right w-48">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">รวม (คาดการณ์)</span>
                      </th>
                      <th className="px-4 py-4 w-16 text-center"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredRows.length === 0 ? (
                        <tr>
                            <td colSpan="9" className="px-6 py-20 text-center text-gray-400 bg-white">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                                        <LocalShippingIcon sx={{ fontSize: 32, opacity: 0.3 }} />
                                    </div>
                                    <span className="font-medium">ยังไม่มีรายการสินค้า</span>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        filteredRows.map((row, index) => {
                            const isEditable = !isClosedOrCanceled && !row.unlimitedWeight;
                            
                            return (
                                <tr key={index} className={`group hover:bg-blue-50/20 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                                    <td className="px-4 py-4">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:text-blue-700 transition-colors">
                                            {row.itemCode || "-"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="text-sm font-medium text-gray-500">
                                            {row.detail}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{row.product}</span>
                                            <span className="text-xs text-gray-400 mt-0.5">หน่วย: {row.unit}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                         <div className="flex justify-center">
                                            <Checkbox 
                                                size="small"
                                                disabled={isClosedOrCanceled}
                                                checked={!!row.unlimitedWeight}
                                                onChange={(e) => handleUpdateRow(index, { unlimitedWeight: e.target.checked })}
                                                sx={{ '&.Mui-checked': { color: '#2563eb' } }}
                                            />
                                         </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="text-sm font-medium text-gray-500">{formatNumber(row.actualWeight)}</span>
                                            <span className="text-gray-300 font-light">/</span>
                                            {row.unlimitedWeight ? (
                                                <span className="text-sm text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded">Unlimited</span>
                                            ) : (
                                                <div className="relative w-28">
                                                    {isEditable ? (
                                                        <input
                                                            type="number"
                                                            value={row.weight}
                                                            onChange={(e) => handleUpdateRow(index, { weight: e.target.value })}
                                                            onFocus={(e) => e.target.select()}
                                                            className="w-full px-3 py-1.5 text-right text-sm font-bold bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-gray-900 shadow-sm transition-all"
                                                        />
                                                    ) : (
                                                        <span className="text-sm font-bold text-gray-900 block text-right px-3 py-1.5 bg-gray-50 rounded-lg border border-transparent">
                                                            {formatNumber(row.weight)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        {row.unlimitedWeight ? (
                                             <span className="text-gray-300">-</span>
                                        ) : (
                                            <span className={`text-sm font-bold px-2 py-1 rounded ${
                                                formatNumber(row.shortWeight).includes("-") 
                                                ? "bg-red-50 text-red-600" 
                                                : "text-gray-700"
                                            }`}>
                                                {formatNumber(row.shortWeight)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4">
                                         <div className="flex justify-end">
                                             {!isClosedOrCanceled ? (
                                                 <div className="relative w-32">
                                                     <input
                                                        type="number"
                                                        value={row.pricePerUnit}
                                                        onChange={(e) => handleUpdateRow(index, { pricePerUnit: e.target.value })}
                                                        onFocus={(e) => e.target.select()}
                                                        className="w-full px-3 py-1.5 text-right text-sm font-bold bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-blue-700 shadow-sm transition-all"
                                                    />
                                                    <span className="absolute left-2 top-1.5 text-gray-400 text-xs font-light">฿</span>
                                                 </div>
                                             ) : (
                                                 <span className="text-sm text-gray-900 font-bold">฿ {formatNumber(row.pricePerUnit)}</span>
                                             )}
                                         </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className="text-base font-bold text-gray-900 block">
                                            {formatNumber(row.predictPrice)}
                                        </span>
                                        <span className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">THB</span>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        {!isClosedOrCanceled && (
                                            <button 
                                                onClick={() => handleDelete(index)}
                                                className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                  </tbody>
                  {filteredRows.length > 0 && (
                       <tfoot className="bg-slate-50 border-t border-gray-200">
                           <tr className="divide-x divide-gray-200/50">
                               <td className="px-6 py-4 text-right" colSpan={4}>
                                   <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">รวมท้ายตาราง</span>
                               </td>
                               <td className="px-4 py-4 text-right">
                                   <div className="flex flex-col gap-1 items-end">
                                       <div className="flex items-center gap-2 text-xs text-gray-400 font-medium uppercase">
                                           <span>จริง</span>
                                           <span className="w-12 text-right text-gray-600 font-bold">{formatNumber(summaryData.sumActual)}</span>
                                       </div>
                                       <div className="flex items-center gap-2 text-xs text-gray-400 font-medium uppercase">
                                           <span>SO</span>
                                           <span className="w-12 text-right text-gray-900 font-bold">{formatNumber(summaryData.sumWeight)}</span>
                                       </div>
                                   </div>
                               </td>
                               <td className="px-4 py-4 text-right align-top">
                                   <span className="text-sm font-bold text-red-600">{formatNumber(summaryData.sumShort)}</span>
                               </td>
                               <td className="px-4 py-4 text-right align-top text-gray-400">-</td>
                               <td className="px-6 py-4 text-right align-top bg-blue-50/30">
                                   <span className="text-xl font-bold text-blue-700">{formatNumber(summaryData.sumPredict)}</span>
                                   <span className="text-xs text-blue-400 block font-semibold mt-1">TOTAL THB</span>
                               </td>
                               <td></td>
                           </tr>
                       </tfoot>
                  )}
                </table>
           </div>
        </div>

        {/* 4. Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-24">
            {/* Left: Remarks */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col gap-3">
                 <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">ข้อความปิดท้าย / หมายเหตุ</h3>
                 <textarea
                     rows={6}
                     disabled={isClosedOrCanceled}
                     className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm resize-none
                         ${isClosedOrCanceled ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-slate-50 text-gray-700 border-slate-200'}
                     `}
                     placeholder="ระบุข้อความเพิ่มเติม..."
                     value={detail2}
                     onChange={(e) => setDetail2(e.target.value)}
                 />
            </div>

            {/* Right: Summary */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-center">
                 <div className="space-y-4">
                     <div className="flex justify-between items-center pb-4 border-b border-dashed border-gray-100">
                         <span className="text-gray-500 font-medium">รวมน้ำหนัก SO ทั้งหมด</span>
                         <span className="text-lg font-bold text-gray-900">{formatNumber(summaryData.sumWeight)} <span className="text-sm text-gray-400 font-normal">กก.</span></span>
                     </div>
                     <div className="flex justify-between items-center pb-4 border-b border-dashed border-gray-100">
                         <span className="text-gray-500 font-medium">รวมน้ำหนักจริง</span>
                         <span className="text-lg font-bold text-gray-900">{formatNumber(summaryData.sumActual)} <span className="text-sm text-gray-400 font-normal">กก.</span></span>
                     </div>
                     <div className="flex justify-between items-center pt-2">
                         <span className="text-lg font-bold text-gray-800">ราคารวมโดยประมาณ</span>
                         <div className="text-right">
                             <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                                 {formatNumber(summaryData.sumPredict)}
                             </span>
                             <span className="text-sm text-gray-400 font-bold ml-2">THB</span>
                         </div>
                     </div>
                 </div>
            </div>
        </div>

        {/* 5. Sticky Footer */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-200 z-40 flex justify-center shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
             <div className="max-w-[1600px] w-full flex justify-end items-center gap-3">
                 {/* Print button - shown but potentially disabled if logic missing, or actionable if we add export */}
                 {/* Re-using icon but no action for now as per instructions to refactor UI. 
                     If export needed, we'd need to implementing exportSaleOrder.
                 */}
                  {/*
                 <button
                     className="px-6 py-3 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 font-bold rounded-2xl shadow-sm transition-all active:scale-95 flex items-center gap-2"
                     onClick={() => toast("Export function not implemented yet")}
                  >
                     <PrintIcon />
                     พิมพ์
                  </button>
                  */}

                 {isClosedOrCanceled ? (
                      <button
                        onClick={() => navigate(-1)}
                        className="px-8 py-3 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-2xl shadow-lg shadow-gray-200 transition-all active:scale-95 flex items-center gap-2"
                      >
                         <ArrowBackIcon />
                         กลับ
                      </button>
                 ) : (
                      <>
                          <button
                           onClick={() => navigate(-1)}
                           className="px-6 py-3 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold rounded-2xl shadow-sm transition-all active:scale-95"
                         >
                            ยกเลิก
                         </button>
                         {/* Close Button */}
                         {statusFilter !== "close" && (
                             <button
                               onClick={() => {
                                   if(window.confirm("ยืนยันการปิดใบขาย? ท่านจะไม่สามารถแก้ไขได้อีก")) {
                                       handleSave("close");
                                   }
                               }}
                               className="px-6 py-3 bg-red-50 text-red-600 hover:bg-red-100 font-bold rounded-2xl transition-all active:scale-95"
                             >
                                 ปิดใบขาย
                             </button>
                         )}
                         <button
                           onClick={() => handleSave()}
                           className="px-10 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all active:scale-95 flex items-center gap-3"
                         >
                            <SaveIcon />
                            บันทึก
                         </button>
                      </>
                 )}
             </div>
        </div>

        <ProductSelectDialog
            open={openProdDialog}
            onClose={() => setOpenProdDialog(false)}
            onConfirm={handleConfirmProducts}
            products={products}
            categories={categories}
            initialSelectedIds={rowsData.map(r => r.productId)}
            multi
        />

        {/* Dialog: เลือกลูกค้า → รถ */}
        <Dialog
            open={openCustDialog}
            onClose={() => setOpenCustDialog(false)}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: { borderRadius: "16px", height: "80vh" }
            }}
        >
            <DialogTitle className="bg-white border-b border-slate-100 p-6">
                <div className="flex justify-between items-center">
                    {modalStep === "customer" ? (
                        <>
                            <h2 className="text-2xl font-bold text-gray-800">เลือกลูกค้า</h2>
                            <div className="relative w-80">
                                <input
                                    type="text"
                                    placeholder="ค้นหาชื่อ..."
                                    value={searchCustomer}
                                    onChange={(e) => setSearchCustomer(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-none rounded-xl focus:ring-2 focus:ring-blue-500/20 text-sm transition-all"
                                />
                                <SearchIcon className="absolute left-3 top-2.5 text-gray-400" />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-3">
                             <button onClick={() => setModalStep("customer")} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                 <ArrowBackIcon />
                             </button>
                             <h2 className="text-2xl font-bold text-gray-800">เลือกรถของ <span className="text-blue-600">{selCustomer?.name}</span></h2>
                        </div>
                    )}
                </div>
            </DialogTitle>
            <DialogContent dividers className="bg-slate-50/50 p-6">
                {modalStep === "customer" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {customers
                            .filter((c) => c.name.toLowerCase().includes(searchCustomer.toLowerCase()))
                            .map((c) => (
                                <div 
                                    key={c.id} 
                                    className="bg-white p-5 border border-slate-200 rounded-2xl hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/10 cursor-pointer transition-all flex items-center gap-4 group"
                                    onClick={() => {
                                        setSelCustomer(c);
                                        if (c.cars?.length) {
                                            setModalStep("car");
                                        } else {
                                            setSelCar(null);
                                            setOpenCustDialog(false);
                                            setSnackNoCar(true);
                                        }
                                    }}
                                >
                                    <Avatar src={c.img} sx={{ width: 64, height: 64 }} className="group-hover:scale-105 transition-transform border-4 border-slate-50">
                                         <PersonIcon />
                                    </Avatar>
                                    <div>
                                        <p className="font-bold text-lg text-gray-900 group-hover:text-blue-700 transition-colors line-clamp-1">{c.name}</p>
                                        <p className="text-sm text-gray-400 group-hover:text-gray-500 mt-1">
                                            {c.personType === "corporate" ? "นิติบุคคล" : "บุคคลธรรมดา"}
                                        </p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                         {selCustomer?.cars?.map((car) => (
                             <div 
                                key={car.id}
                                className="bg-white p-6 border border-slate-200 rounded-2xl hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-all flex items-center gap-4 group"
                                onClick={() => {
                                    setSelCar(car);
                                    setOpenCustDialog(false);
                                }}
                             >
                                 <div className="p-3 bg-blue-100 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                                     <DirectionsCarIcon fontSize="medium" />
                                 </div>
                                 <div>
                                     <p className="font-bold text-xl text-gray-900 group-hover:text-blue-700">{car.license}</p>
                                     <p className="text-sm text-gray-400">{car.type} — {car.province}</p>
                                 </div>
                             </div>
                         ))}
                    </div>
                )}
            </DialogContent>
            {modalStep === "car" && (
                <DialogActions className="p-4 bg-white border-t border-slate-100">
                    <button 
                        onClick={() => { setSelCar(null); setOpenCustDialog(false); }}
                        className="px-6 py-2.5 text-gray-500 hover:bg-slate-100 rounded-xl font-bold text-sm transition-all"
                    >
                        ไม่ระบุรถ (ข้ามขั้นตอน)
                    </button>
                </DialogActions>
            )}
        </Dialog>

        <Snackbar
            open={snackNoCar}
            autoHideDuration={3000}
            onClose={() => setSnackNoCar(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
            <Alert severity="warning" onClose={() => setSnackNoCar(false)} className="rounded-xl shadow-lg">ลูกค้ารายนี้ยังไม่มีรถในระบบ</Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default SoManagementEdit;
